import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  User, 
  Building2, 
  CreditCard, 
  Save, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  Loader2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Sliders
} from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../contexts/SettingsContext';
import api from '../utils/api';
import DashboardPageLayout from './DashboardPageLayout';
import { getPricing } from '../config/pricing';
import { TIMEZONES, getTimezonesForCountry, DEFAULT_TIMEZONE_USA } from '../utils/timezones';

const SettingsTab = ({ activeSubView }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { dateFormat, timeFormat, updateDateFormat, updateTimeFormat } = useSettings();
  const [paymentHistory, setPaymentHistory] = useState([]);
  const starterPlan = getPricing('starter', false); // Use demo pricing
  
  // Organization data
  const [orgData, setOrgData] = useState({
    name: '',
    slug: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    city: '',
    timezone: DEFAULT_TIMEZONE_USA,
    country: 'USA'
  });
  
  // Subscription data
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    fetchProfileData();
    
    // Check if returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'canceled') {
      toast.error('Payment was canceled');
      window.history.replaceState({}, '', '/admin');
    }
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      // Fetch organization data
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = userData.organizationId;
      
      if (orgId) {
        const orgResponse = await api.get(`/organizations/${orgId}`);
        setOrgData(orgResponse.data);
      }
      
      // Fetch subscription status
      const subResponse = await api.get('/subscriptions/my-status');
      setSubscriptionStatus(subResponse.data);
      
      // Fetch payment history
      try {
        const historyResponse = await api.get('/subscriptions/payment-history');
        setPaymentHistory(historyResponse.data.payments || []);
      } catch (err) {
        console.error('Error fetching payment history:', err);
      }
      
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      const updateData = {
        name: orgData.name,
        ownerName: orgData.ownerName,
        ownerEmail: orgData.ownerEmail,
        ownerPhone: orgData.ownerPhone,
        city: orgData.city,
        timezone: orgData.timezone,
        country: orgData.country
      };
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = userData.organizationId;
      
      await api.put(`/organizations/${orgId}`, updateData);
      
      toast.success('Profile updated successfully!');
      fetchProfileData();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setProcessing(false);
      return;
    }

    setProcessing(true);

    try {
      const response = await api.get(`/subscriptions/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('🎉 Subscription activated! Welcome to BookingFlows!');
        fetchProfileData();
        setProcessing(false);
        window.history.replaceState({}, '', '/admin');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setProcessing(false);
        window.history.replaceState({}, '', '/admin');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please refresh the page.');
      setProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setProcessing(true);
      const originUrl = window.location.origin;
      
      // Create programmatic checkout session
      const response = await api.post('/subscriptions/create-checkout', null, {
        params: {
          package_id: 'starter',
          origin_url: originUrl
        }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout. Please try again.');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      return;
    }

    try {
      setProcessing(true);
      await api.post('/subscriptions/cancel');
      toast.success('Subscription canceled');
      fetchProfileData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>;
      case 'canceled':
        return <Badge className="bg-gray-500 text-white"><XCircle className="w-3 h-3 mr-1" /> Canceled</Badge>;
      case 'past_due':
        return <Badge className="bg-yellow-500 text-white"><AlertCircle className="w-3 h-3 mr-1" /> Past Due</Badge>;
      default:
        return <Badge className="bg-gray-400 text-white"><Clock className="w-3 h-3 mr-1" /> Inactive</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Profile View
  if (activeSubView === 'settings-profile') {
    return (
      <DashboardPageLayout
        title="Profile Settings"
        subtitle="Manage your organization and owner information"
      >
        <div className="space-y-6">
          {processing && (
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">Processing payment...</p>
                    <p className="text-sm text-blue-700">Please wait while we verify your subscription</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

        {/* Organization Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Organization
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="orgName">Organization Name *</Label>
                <Input
                  id="orgName"
                  value={orgData.name}
                  onChange={(e) => setOrgData({...orgData, name: e.target.value})}
                  placeholder="e.g., Austin Asset Tours"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Organization Slug</Label>
                <Input
                  id="slug"
                  value={orgData.slug}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-gray-500">Cannot be changed after creation</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input
                    id="city"
                    value={orgData.city || ''}
                    onChange={(e) => setOrgData({...orgData, city: e.target.value})}
                    placeholder="e.g., Austin"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <select
                  id="timezone"
                  value={orgData.timezone}
                  onChange={(e) => setOrgData({...orgData, timezone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {/* Show country-specific timezones first */}
                  <optgroup label={orgData.country === 'UAE' ? '🇦🇪 UAE Timezones' : '🇺🇸 US Timezones'}>
                    {getTimezonesForCountry(orgData.country).map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="All Timezones">
                    {TIMEZONES.filter(tz => !getTimezonesForCountry(orgData.country).some(ctz => ctz.value === tz.value)).map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Owner Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Owner Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="ownerName">Full Name *</Label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input
                    id="ownerName"
                    value={orgData.ownerName}
                    onChange={(e) => setOrgData({...orgData, ownerName: e.target.value})}
                    placeholder="Your full name"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerEmail">Email Address *</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input
                    id="ownerEmail"
                    type="email"
                    value={orgData.ownerEmail}
                    onChange={(e) => setOrgData({...orgData, ownerEmail: e.target.value})}
                    placeholder="your@email.com"
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ownerPhone">Phone Number</Label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input
                    id="ownerPhone"
                    value={orgData.ownerPhone || ''}
                    onChange={(e) => setOrgData({...orgData, ownerPhone: e.target.value})}
                    placeholder="+1-555-0100"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSaveProfile}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
      </DashboardPageLayout>
    );
  }

  // Billing View
  if (activeSubView === 'settings-billing') {
    return (
      <DashboardPageLayout
        title="Billing & Subscription"
        subtitle="Manage your subscription plan and payment details"
      >
        <div className="space-y-6">
        {processing && (
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">Processing payment...</p>
                  <p className="text-sm text-blue-700">Please wait while we verify your subscription</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription & Billing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Status */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-600">Current Status</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-lg font-semibold capitalize">
                    {subscriptionStatus?.subscriptionPlan || 'Free'}
                  </span>
                  {getStatusBadge(subscriptionStatus?.subscriptionStatus)}
                </div>
              </div>
              {subscriptionStatus?.subscriptionStartDate && (
                <div className="text-right">
                  <p className="text-sm text-gray-600">Active Since</p>
                  <p className="text-sm font-medium">
                    {new Date(subscriptionStatus.subscriptionStartDate).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>

            {/* Subscription Actions */}
            {subscriptionStatus?.subscriptionStatus !== 'active' ? (
              <div className="border-2 border-blue-200 bg-blue-50 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-blue-900 text-xl">{starterPlan.name}</h3>
                    <p className="text-blue-700 mt-1">{starterPlan.description}</p>
                    <div className="mt-3">
                      <span className="text-4xl font-bold text-blue-900">{starterPlan.formattedPrice}</span>
                      <span className="text-blue-700 text-lg">/{starterPlan.period}</span>
                      <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">DEMO PRICING</span>
                    </div>
                    <ul className="mt-4 space-y-2">
                      {starterPlan.features.slice(0, 5).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-blue-800">
                          <CheckCircle className="w-4 h-4 text-blue-600" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <Button
                  onClick={handleSubscribe}
                  disabled={processing}
                  className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
                  size="lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border-2 border-green-200 bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <h3 className="font-bold text-green-900 text-lg">Active Subscription</h3>
                  </div>
                  <p className="text-green-700">You're all set! Enjoying full access to BookingFlows.</p>
                  <p className="text-sm text-green-600 mt-2">
                    Billing: $0.89/month (demo pricing) • 100 actions/month • Renews automatically
                  </p>
                </div>

                <Button
                  onClick={handleCancelSubscription}
                  disabled={processing}
                  variant="outline"
                  className="w-full border-red-200 text-red-600 hover:bg-red-50"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel Subscription
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment History */}
        {paymentHistory && paymentHistory.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Payment History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {paymentHistory.map((payment, index) => (
                  <div 
                    key={payment.id || index} 
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${
                        payment.status === 'paid' ? 'bg-green-100' : 'bg-yellow-100'
                      }`}>
                        {payment.status === 'paid' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-yellow-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          ${payment.amount.toFixed(2)} {payment.currency}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(payment.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        payment.status === 'paid' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }>
                        {payment.status === 'paid' ? 'Paid' : 'Pending'}
                      </Badge>
                      {payment.invoiceUrl && (
                        <a
                          href={payment.invoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-xs text-blue-600 hover:text-blue-800 mt-1"
                        >
                          View Invoice →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </DashboardPageLayout>
    );
  }

  // Preferences View
  if (activeSubView === 'settings-preferences') {
    return (
      <DashboardPageLayout
        title="Preferences"
        subtitle="Customize your display settings and defaults"
      >
        <Card>
          <CardContent className="pt-6 space-y-6">
          {/* Date Format */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Date Format
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => updateDateFormat('MM/DD/YYYY')}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  dateFormat === 'MM/DD/YYYY'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">MM/DD/YYYY</div>
                <div className="text-sm text-gray-600 mt-1">10/17/2025</div>
              </button>
              <button
                onClick={() => updateDateFormat('DD/MM/YYYY')}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  dateFormat === 'DD/MM/YYYY'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">DD/MM/YYYY</div>
                <div className="text-sm text-gray-600 mt-1">17/10/2025</div>
              </button>
              <button
                onClick={() => updateDateFormat('YYYY-MM-DD')}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  dateFormat === 'YYYY-MM-DD'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">YYYY-MM-DD</div>
                <div className="text-sm text-gray-600 mt-1">2025-10-17</div>
              </button>
            </div>
          </div>

          {/* Time Format */}
          <div className="space-y-3 pt-4 border-t">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Time Format
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => updateTimeFormat('12h')}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  timeFormat === '12h'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">12-hour</div>
                <div className="text-sm text-gray-600 mt-1">11:00 PM</div>
              </button>
              <button
                onClick={() => updateTimeFormat('24h')}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  timeFormat === '24h'
                    ? 'border-blue-600 bg-blue-50 text-blue-900'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium">24-hour</div>
                <div className="text-sm text-gray-600 mt-1">23:00</div>
              </button>
            </div>
          </div>

          {/* Success Message */}
          <div className="pt-4 border-t">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Preferences are saved automatically</span>
            </div>
          </div>
        </CardContent>
      </Card>
      </DashboardPageLayout>
    );
  }

  return null;
};

export default SettingsTab;
